import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useRouteContext } from '@tanstack/react-router';
import { Button, ConfigProvider, Flex, Grid, message, Spin, Tag, Tooltip } from 'antd';
import { CircleDot, GripVertical, KanbanIcon, ListChecks } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import {
  getListDealBoardColumnUrl,
  listDealBoardColumn,
  reorderCustomFieldOptions,
  updateDealStage,
  useAutocompleteLeads,
} from '../../../api';
import { originalAxiosInstance } from '../../../api/axios.instance';
import {
  DEFAULT_OPTION_COLOR,
  PILL_TONE_STYLES,
  TONE_BY_STAGE_COLOR,
} from '../../../modules/custom-field/constants/option-colors';
import { DealsBoardCard } from '../../../modules/deal/components/deals-board/DealsBoardCard';
import {
  DEAL_BOARD_PREVIEW_STAGES,
  dealBoardPreviewQueryKey,
  fetchDealBoardPreviewPage,
} from '../../../modules/deal/constants';
import { formatFullAmount } from '../../../modules/deal/utils/format-amount';
import { GlobalSearch } from '../../../modules/global-search/components';
import { HeaderActions } from '../../../modules/navigation/components/header-actions';
import { CreateRecordDrawer } from '../../../modules/records/components/create-record-drawer';
import { CrmEmptyStateLayout } from '../../../modules/records/components/empty-states';
import { SmartViewTabs, useSmartView } from '../../../modules/smart-view';
import { Chip } from '../../../ui/components/chip';
import { GroupByButton, type GroupByGroup } from '../../../ui/components/group-by-button';
import {
  RecordBoard,
  type RecordBoardColumn,
  type RecordBoardColumnStats,
} from '../../../ui/components/record-board';
import { SortByButton } from '../../../ui/components/sort-by-button';
import { TableSurface } from '../../../ui/components/table-surface';
import { useDebouncedValue, useDensity, useNavigationPosition } from '../../../ui/hooks';
import { useLayoutInsets } from '../../../ui/providers/layout-insets';
import { formatCompactNumber } from '../../../ui/utils/format-number';
import './pipeline.css';

import type {
  ListDealBoardColumnBodyDto,
  ListDealBoardColumnResponseDtoItemsItem,
} from '../../../api';
import type { GetPipelineMetadataResponseDto } from '../../../api/generated/model/getPipelineMetadataResponseDto';

const STAGE_GROUPING_ID = 'stage';
const NONE_COLUMN_KEY = '__none__';

type GroupingMode = { type: 'stage' } | { type: 'single_select' | 'multi_select'; fieldId: string };
type HeaderProps = {
  dragHandleProps?: {
    attributes: Record<string, unknown>;
    listeners: Record<string, unknown>;
  };
};

const PipelineColumnHeader = memo(function PipelineColumnHeader({
  column,
  stats,
  dragHandleProps,
}: {
  column: RecordBoardColumn;
  stats: RecordBoardColumnStats;
  dragHandleProps?: HeaderProps['dragHandleProps'];
}) {
  const chipColor = column.color ?? DEFAULT_OPTION_COLOR;
  const totalAmount = stats.totalAmount ?? 0;

  return (
    <Flex align="center" justify="space-between" gap={6}>
      <Flex align="center" gap={6} style={{ minWidth: 0, overflow: 'hidden' }}>
        <Flex
          align="center"
          data-testid="board-column-grip"
          style={{ cursor: 'grab', color: 'var(--ant-color-text-quaternary)', flexShrink: 0 }}
          {...(dragHandleProps?.attributes ?? {})}
          {...(dragHandleProps?.listeners ?? {})}
        >
          <GripVertical size={14} />
        </Flex>
        <Chip
          label={column.label}
          color={chipColor}
          variant="deal-stage"
          style={PILL_TONE_STYLES[TONE_BY_STAGE_COLOR[chipColor] ?? 'gray']}
        />
      </Flex>
      <Flex align="center" style={{ flexShrink: 0, gap: 8 }}>
        <Tooltip title={`$${formatFullAmount(totalAmount)}`}>
          <Tag className="font-mono" style={{ fontSize: 11, padding: '0 7px' }}>
            ${formatCompactNumber(totalAmount)}
          </Tag>
        </Tooltip>
        <Tag className="font-mono" style={{ fontSize: 11, padding: '0 6px' }}>
          {stats.total}
        </Tag>
      </Flex>
    </Flex>
  );
});

const PipelineRoute = () => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { selectedOrganization } = useRouteContext({ from: '/_authenticated/_app' });
  const organizationId = selectedOrganization.id;
  const insets = useLayoutInsets();
  const navigate = useNavigate({ from: '/pipeline' });

  const { groupBy: groupingId = STAGE_GROUPING_ID, search: pipelineSearch = '' } =
    Route.useSearch();

  const smartView = useSmartView({ organizationId, entityType: 'deal', allColumns: [] });
  const activeFilter = (smartView.activeView?.filter ?? null) as ListDealBoardColumnBodyDto['filter'];
  const setGroupingId = (next: string) =>
    navigate({
      search: (prev) => ({ ...prev, groupBy: next === STAGE_GROUPING_ID ? undefined : next }),
      replace: true,
      resetScroll: false,
    });

  const [createRecordOpen, setCreateRecordOpen] = useState(false);
  const { data: orgLeadsData, isPending: isOrgLeadsPending } = useAutocompleteLeads(
    organizationId,
    { take: 1 },
  );

  const { data: metadata } = useQuery({
    queryKey: ['pipeline-metadata', organizationId],
    queryFn: async () => {
      const resp = await originalAxiosInstance.get<GetPipelineMetadataResponseDto>(
        `/organizations/${organizationId}/pipeline/metadata`,
      );
      return resp.data;
    },
    enabled: !!organizationId,
  });

  const [debouncedPipelineSearch] = useDebouncedValue(pipelineSearch, 300);

  const { isSideNav } = useNavigationPosition();
  const { density, toolbarTheme } = useDensity('kanban');
  const handleSearchChange = useCallback(
    (val: string) => {
      navigate({
        search: (prev) => ({ ...prev, search: val || undefined }),
        replace: true,
        resetScroll: false,
      });
    },
    [navigate],
  );

  const trimmedSearch = debouncedPipelineSearch.trim();
  const isEmptyPreview = !isOrgLeadsPending && orgLeadsData?.data.items.length === 0 && !trimmedSearch;

  const grouping = useMemo<GroupingMode>(() => {
    if (metadata?.multiSelectFields.some((field) => field.id === groupingId)) {
      return { type: 'multi_select', fieldId: groupingId };
    }
    if (metadata?.singleSelectFields.some((field) => field.id === groupingId)) {
      return { type: 'single_select', fieldId: groupingId };
    }
    return { type: 'stage' };
  }, [metadata, groupingId]);

  const groupByGroups = useMemo<GroupByGroup[]>(() => {
    if (!metadata) return [];
    return [
      {
        key: 'single',
        label: 'SINGLE SELECT',
        options: [
          {
            id: STAGE_GROUPING_ID,
            label: 'Pipeline Stage',
            description: `${metadata.stages.length} stages`,
            icon: <KanbanIcon size={14} />,
          },
          ...metadata.singleSelectFields.map((field) => ({
            id: field.id,
            label: field.name,
            description: `${field.options.length} options`,
            icon: <CircleDot size={14} />,
          })),
        ],
      },
      ...(metadata.multiSelectFields.length > 0
        ? [
            {
              key: 'multi',
              label: 'MULTI SELECT',
              options: metadata.multiSelectFields.map((field) => ({
                id: field.id,
                label: field.name,
                description: `${field.options.length} options · multi`,
                icon: <ListChecks size={14} />,
              })),
            },
          ]
        : []),
    ];
  }, [metadata]);

  const columns = useMemo<RecordBoardColumn[]>(() => {
    if (!metadata) return [];
    if (grouping.type === 'stage') {
      return [...metadata.stages]
        .sort((a, b) => a.order - b.order)
        .map((stage) => ({ key: stage.id, label: stage.name, color: stage.color }));
    }
    const field = [...metadata.singleSelectFields, ...metadata.multiSelectFields].find(
      (candidate) => candidate.id === grouping.fieldId,
    );
    return [
      ...[...(field?.options ?? [])]
        .sort((a, b) => a.order - b.order)
        .map((option) => ({
          key: option.id,
          label: option.label,
          color: option.color ?? DEFAULT_OPTION_COLOR,
        })),
      { key: NONE_COLUMN_KEY, label: 'None', color: DEFAULT_OPTION_COLOR },
    ];
  }, [metadata, grouping]);

  const groupBy = useMemo(
    () =>
      grouping.type === 'stage'
        ? ({ field: 'stage' } as const)
        : ({ field: 'custom_field', customFieldId: grouping.fieldId } as const),
    [grouping],
  );

  const queryKey = useCallback(
    (columnKey: string) =>
      [
        getListDealBoardColumnUrl(organizationId),
        groupBy,
        columnKey,
        trimmedSearch,
        activeFilter,
        smartView.sort,
      ] as const,
    [organizationId, groupBy, trimmedSearch, activeFilter, smartView.sort],
  );

  const queryClient = useQueryClient();

  const handleColumnOrderChange = useCallback(
    async (orderedColumnIds: string[]) => {
      try {
        if (grouping.type === 'stage') {
          await Promise.all(
            orderedColumnIds.map((dealStageId, order) =>
              updateDealStage(organizationId, dealStageId, { order }),
            ),
          );
        } else {
          const orderedOptionIds = orderedColumnIds.filter((key) => key !== NONE_COLUMN_KEY);
          await reorderCustomFieldOptions(organizationId, grouping.fieldId, { orderedOptionIds });
        }

        queryClient.invalidateQueries({ queryKey: ['pipeline-metadata', organizationId] });
      } catch {
        message.error('Failed to save column order');
      }
    },
    [grouping, organizationId, queryClient],
  );

  const fetchPage = useCallback(
    async (columnKey: string, skip: number, take: number) => {
      const { data } = await listDealBoardColumn(organizationId, {
        groupBy,
        columnKey,
        skip,
        take,
        ...(trimmedSearch ? { search: trimmedSearch } : {}),
        ...(activeFilter ? { filter: activeFilter } : {}),
        ...(smartView.sort ? { sort: smartView.sort } : {}),
      });
      return { items: data.items, total: data.total, totalAmount: data.totalAmount };
    },
    [organizationId, groupBy, trimmedSearch, activeFilter, smartView.sort],
  );

  const handleMove = useCallback(
    async (
      deal: ListDealBoardColumnResponseDtoItemsItem,
      fromColumnKey: string,
      toColumnKey: string,
    ) => {
      if (grouping.type === 'stage') {
        await originalAxiosInstance.patch(`/organizations/${organizationId}/deals/${deal.id}`, {
          dealStageId: toColumnKey,
        });
        return;
      }

      const existing = deal.customFields.find((f) => f.customFieldId === grouping.fieldId);
      const currentRaw =
        existing?.value && 'string' in existing.value ? existing.value.string : null;

      const nextValue =
        grouping.type === 'single_select'
          ? toColumnKey === NONE_COLUMN_KEY
            ? null
            : toColumnKey
          : (() => {
              const values = (currentRaw ?? '')
                .split(',')
                .map((v) => v.trim())
                .filter(Boolean)
                .filter((v) => v !== fromColumnKey);
              if (toColumnKey !== NONE_COLUMN_KEY && !values.includes(toColumnKey)) {
                values.push(toColumnKey);
              }
              return values.length > 0 ? values.join(',') : null;
            })();

      if (existing) {
        await originalAxiosInstance.patch(
          `/organizations/${organizationId}/deals/${deal.id}/custom-field/${existing.id}`,
          { value: { string: nextValue } },
        );
      } else if (nextValue) {
        await originalAxiosInstance.post(
          `/organizations/${organizationId}/deals/${deal.id}/custom-field`,
          { link: { customFieldId: grouping.fieldId, value: { string: nextValue } } },
        );
      }
    },
    [grouping, organizationId],
  );

  const renderDealCard = useCallback(
    (deal: ListDealBoardColumnResponseDtoItemsItem, { isDragging }: { isDragging: boolean }) => (
      <DealsBoardCard deal={deal} isDragging={isDragging} />
    ),
    [],
  );

  const renderDealColumnHeader = useCallback(
    (
      column: RecordBoardColumn,
      stats: RecordBoardColumnStats,
      headerProps: { canDragColumn: boolean; dragHandleProps?: unknown },
    ) => (
      <PipelineColumnHeader
        column={column}
        stats={stats}
        dragHandleProps={(headerProps as HeaderProps).dragHandleProps}
      />
    ),
    [],
  );

  return (
    <Flex vertical style={{ height: `calc(100dvh - ${insets.top}px)`, minHeight: 0 }}>
      <Flex
        vertical={isMobile}
        align={isMobile ? 'stretch' : isSideNav ? 'center' : 'flex-end'}
        justify="space-between"
        gap={isMobile ? 0 : 8}
        style={{
          padding: isMobile
            ? `8px 12px ${isSideNav ? 12 : 0}px`
            : isSideNav
              ? '16px 24px 8px 0'
              : '0 24px',
          minHeight: isMobile || isSideNav ? undefined : density === 'compact' ? 40 : 44,
          backgroundColor: 'var(--ant-color-bg-layout)',
          flexShrink: 0,
        }}
      >
        <Flex
          align="center"
          gap={8}
          flex={1}
          style={{
            width: isMobile ? '100%' : undefined,
            overflowX: isMobile ? 'auto' : undefined,
            scrollbarWidth: 'none',
            order: isMobile ? 2 : undefined,
          }}
          className="pipeline-toolbar-tabs"
        >
          {isSideNav ? (
            <GlobalSearch
              organizationId={organizationId}
              tableSearch={{
                value: pipelineSearch,
                onChange: handleSearchChange,
                placeholder: 'Search pipelines',
              }}
            />
          ) : null}
          <ConfigProvider theme={toolbarTheme}>
            <SmartViewTabs
              smartView={smartView}
              organizationId={organizationId}
              entityType="deal"
              hasData={!isEmptyPreview}
              disabled={isEmptyPreview}
            />
          </ConfigProvider>
        </Flex>
        <Flex
          align="center"
          gap={8}
          justify={isMobile ? 'space-between' : 'flex-end'}
          style={{
            width: isMobile ? '100%' : undefined,
            marginBottom: isSideNav ? 0 : 6,
            order: isMobile ? 1 : undefined,
          }}
        >
          {isSideNav && <HeaderActions organizationId={organizationId} variant="side" />}
          {!isEmptyPreview && (
            <>
              <SortByButton
                testId="pipeline-sort"
                options={[
                  { id: 'deal.name', label: 'Deal name' },
                  { id: 'deal.amount', label: 'Amount' },
                  { id: 'deal.createdAt', label: 'Created' },
                  { id: 'deal.updatedAt', label: 'Updated' },
                ]}
                value={smartView.sort ?? { field: 'deal.createdAt', direction: 'desc' }}
                onChange={(next) =>
                  smartView.handleSortSnapshot(
                    next,
                    (next ? `${next.field}:${next.direction}` : '') !==
                      (smartView.activeView?.sort
                        ? `${smartView.activeView.sort.field}:${smartView.activeView.sort.direction}`
                        : ''),
                  )
                }
              />
              <GroupByButton groups={groupByGroups} value={groupingId} onChange={setGroupingId} />
            </>
          )}
        </Flex>
      </Flex>

      <TableSurface
        className={`table-surface--page-inset${isSideNav ? '' : ' table-surface--page-inset-top-nav'}`}
      >
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
          {metadata || isEmptyPreview ? (
            <div
              data-testid="pipeline-board"
              style={{ height: '100%', opacity: isEmptyPreview ? 0.48 : 1 }}
            >
              <RecordBoard<ListDealBoardColumnResponseDtoItemsItem>
                columns={isEmptyPreview ? DEAL_BOARD_PREVIEW_STAGES : columns}
                queryKey={isEmptyPreview ? dealBoardPreviewQueryKey : queryKey}
                fetchPage={isEmptyPreview ? fetchDealBoardPreviewPage : fetchPage}
                onMove={isEmptyPreview ? undefined : handleMove}
                renderCard={renderDealCard}
                onColumnOrderChange={isEmptyPreview ? undefined : handleColumnOrderChange}
                isSideNav={isSideNav}
                renderColumnHeader={renderDealColumnHeader}
                compact={density === 'compact'}
              />
            </div>
          ) : (
            <Flex align="center" justify="center" style={{ height: '100%' }}>
              <Spin />
            </Flex>
          )}

          {isEmptyPreview && (
            <CrmEmptyStateLayout
              title="Let's get your CRM ready"
              body="Upload your companies and connect your email—then sit back. The system matches people with past conversations and quickly learns how you sell, speak, and operate… so it can engage new companies like you would."
              overlay
            >
              <Flex justify="center" gap={8}>
                <Button type="primary" onClick={() => navigate({ to: '/import/new' })}>
                  Import
                </Button>
                <Button onClick={() => setCreateRecordOpen(true)}>Create Company</Button>
              </Flex>
            </CrmEmptyStateLayout>
          )}
          {createRecordOpen && (
            <CreateRecordDrawer
              organizationId={organizationId}
              onClose={() => setCreateRecordOpen(false)}
            />
          )}
        </div>
      </TableSurface>
    </Flex>
  );
};

export const Route = createFileRoute('/_authenticated/_app/pipeline')({
  component: PipelineRoute,
  validateSearch: (params) =>
    z
      .object({
        groupBy: z.string().optional(),
        search: z.string().optional(),
        smartView: z.string().optional(),
      })
      .parse(params),
});
